import {
    Avatar,
    Badge,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import {
    Deck,
    Edit,
    ExitToApp,
    KeyboardArrowDown,
    LocalAtm,
    MenuBook,
    Notifications,
    Palette,
    Person,
    Store,
    SwapHoriz,
} from "@material-ui/icons"
import { useState } from "react"
import useAppStore from "../../stores/AppStore"
import { useStatusLoja } from "./StatusLoja"

const useStyles = makeStyles(theme => ({
    barra: {
        display: "flex",
        alignItems: "center",
        gap: 2,
    },
    cardapioBtn: {
        color: "#fff",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
    },
    divisor: {
        height: 34,
        alignSelf: "center",
        margin: "0 6px",
        backgroundColor: "rgba(255,255,255,0.3)",
    },
    cell: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "4px 6px 4px 10px",
        borderRadius: 8,
        color: "#fff",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
    },
    logoLoja: {
        width: 34,
        height: 34,
        backgroundColor: "rgba(255,255,255,0.15)",
        color: "#fff",
        "& svg": { fontSize: 22 },
    },
    textos: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        lineHeight: 1.25,
    },
    linhaLoja: {
        display: "flex",
        alignItems: "center",
        gap: 6,
    },
    nomeLoja: {
        fontSize: 14,
        fontWeight: 700,
        maxWidth: 190,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: { maxWidth: 120 },
    },
    usuario: {
        fontSize: 12,
        opacity: 0.85,
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("xs")]: { display: "none" },
    },
    seta: { color: "#fff", padding: 4 },
}))

// Cluster do canto superior direito: [ícone Cardápio] | [Loja + Usuário ▼].
export default function BarraPerfil({
    onMeuPerfil,
    onTrocarEstabelecimento,
    onEditarAplicativo,
    onSair,
    onCaixa,
    onRegistrador,
    onMesas,
    alertasCount = 0,
    onAbrirAlertas,
}) {
    const classes = useStyles()
    const theme = useTheme()
    // No mobile os ícones de atalho (Caixa/Registrador/Mesas/Avisos) vão para DENTRO
    // do menu, para não transbordarem o cabeçalho e sumirem cortados.
    const mobile = useMediaQuery(theme.breakpoints.down("sm"))
    const configs = useAppStore(s => s.configuracoes)
    // O botão de situação vive ao lado do seletor de Operação; aqui só resta o pontinho do mobile.
    const { status: statusExibido } = useStatusLoja()

    const usuario = configs?.user?.usuario || "Meu perfil"
    const urlSite = configs?.user?.parametros?.urlSiteAplicativo
    const linkCardapio = urlSite ? `https://${urlSite}` : null

    const estabelecimento = configs?.user?.parametros?.estabelecimento
    const nomeEstabelecimento =
        estabelecimento?.nome || estabelecimento?.nomeAlternativo || "Selecionar estabelecimento"

    const [anchorMenu, setAnchorMenu] = useState(null)

    const abrirMenu = e => setAnchorMenu(e.currentTarget)
    const fecharMenu = () => setAnchorMenu(null)

    return (
        <div className={classes.barra}>
            {/* Atalhos rápidos: Recebedor, Registrador e Mesas (apenas ícones).
                No mobile eles migram para o menu (abaixo), evitando o corte. */}
            {!mobile && (onCaixa || onRegistrador || onMesas) && (
                <>
                    {onCaixa && (
                        <Tooltip arrow title="Abrir caixa">
                            <IconButton className={classes.cardapioBtn} onClick={() => onCaixa()}>
                                <LocalAtm />
                            </IconButton>
                        </Tooltip>
                    )}
                    {onRegistrador && (
                        <Tooltip arrow title="Abrir registrador de pedidos">
                            <IconButton
                                className={classes.cardapioBtn}
                                onClick={() => onRegistrador()}
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                    )}
                    {onMesas && (
                        <Tooltip arrow title="Abrir tela de mesas">
                            <IconButton className={classes.cardapioBtn} onClick={() => onMesas()}>
                                <Deck />
                            </IconButton>
                        </Tooltip>
                    )}
                    {alertasCount > 0 && onAbrirAlertas && (
                        <Tooltip arrow title="Avisos do sistema">
                            <IconButton
                                className={classes.cardapioBtn}
                                onClick={() => onAbrirAlertas()}
                            >
                                <Badge
                                    badgeContent={alertasCount}
                                    color="error"
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                >
                                    <Notifications />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}
                    <Divider orientation="vertical" flexItem className={classes.divisor} />
                </>
            )}

            {/* Loja + Usuário (abre o menu na seta). No mobile compacta para só o ícone
                + um ponto de status + a seta (nome da loja/usuário ficam no menu). */}
            <div className={classes.cell} onClick={abrirMenu}>
                <Avatar
                    src={estabelecimento?.urlLogo || undefined}
                    variant="rounded"
                    className={classes.logoLoja}
                    alt={nomeEstabelecimento}
                >
                    <Store />
                </Avatar>
                {mobile ? (
                    statusExibido && (
                        <span
                            title={statusExibido.label}
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: statusExibido.cor,
                                flexShrink: 0,
                            }}
                        />
                    )
                ) : (
                    <span className={classes.textos}>
                        <span className={classes.linhaLoja}>
                            <span className={classes.nomeLoja}>{nomeEstabelecimento}</span>
                        </span>
                        <span className={classes.usuario}>{usuario}</span>
                    </span>
                )}
                <KeyboardArrowDown className={classes.seta} />
            </div>

            <Menu
                anchorEl={anchorMenu}
                open={Boolean(anchorMenu)}
                onClose={fecharMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                getContentAnchorEl={null}
            >
                {/* No mobile, os atalhos rápidos aparecem aqui (não como ícones soltos). */}
                {mobile && onCaixa && (
                    <MenuItem
                        onClick={() => {
                            fecharMenu()
                            onCaixa()
                        }}
                    >
                        <LocalAtm fontSize="small" style={{ marginRight: 10, color: "#2e9e5b" }} />
                        Abrir caixa
                    </MenuItem>
                )}
                {mobile && onRegistrador && (
                    <MenuItem
                        onClick={() => {
                            fecharMenu()
                            onRegistrador()
                        }}
                    >
                        <Edit fontSize="small" style={{ marginRight: 10, color: "#0277BD" }} />
                        Registrador de pedidos
                    </MenuItem>
                )}
                {mobile && onMesas && (
                    <MenuItem
                        onClick={() => {
                            fecharMenu()
                            onMesas()
                        }}
                    >
                        <Deck fontSize="small" style={{ marginRight: 10, color: "#F59E0B" }} />
                        Mesas
                    </MenuItem>
                )}
                {mobile && alertasCount > 0 && onAbrirAlertas && (
                    <MenuItem
                        onClick={() => {
                            fecharMenu()
                            onAbrirAlertas()
                        }}
                    >
                        <Badge badgeContent={alertasCount} color="error">
                            <Notifications
                                fontSize="small"
                                style={{ marginRight: 10, color: "#DC4C64" }}
                            />
                        </Badge>
                        <span style={{ marginLeft: 6 }}>Avisos do sistema</span>
                    </MenuItem>
                )}
                {mobile && (onCaixa || onRegistrador || onMesas) && <Divider />}
                <MenuItem
                    onClick={() => {
                        fecharMenu()
                        onMeuPerfil?.()
                    }}
                >
                    <Person fontSize="small" style={{ marginRight: 10, color: "#4f46e5" }} />
                    Meu Perfil
                </MenuItem>
                {linkCardapio && (
                    <MenuItem
                        onClick={() => {
                            fecharMenu()
                            window.open(linkCardapio, "_blank", "noopener")
                        }}
                    >
                        <MenuBook fontSize="small" style={{ marginRight: 10, color: "#F59E0B" }} />
                        Cardápio Digital
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => {
                        fecharMenu()
                        onTrocarEstabelecimento?.()
                    }}
                >
                    <SwapHoriz fontSize="small" style={{ marginRight: 10, color: "#0277BD" }} />
                    Trocar Estabelecimento
                </MenuItem>
                {onEditarAplicativo && (
                    <MenuItem
                        onClick={() => {
                            fecharMenu()
                            onEditarAplicativo()
                        }}
                    >
                        <Palette fontSize="small" style={{ marginRight: 10, color: "#14A44D" }} />
                        Editar Aplicativo
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => {
                        fecharMenu()
                        onSair?.()
                    }}
                >
                    <ExitToApp fontSize="small" style={{ marginRight: 10, color: "#DC4C64" }} />
                    Sair
                </MenuItem>
            </Menu>
        </div>
    )
}
